import _defineProperty from "@babel/runtime/helpers/defineProperty";

var _createReducer;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import config from 'config';
import { createAction, createReducer, createSelector, getDateNowFunction, parseDate } from '@techstyle/redux-core';
import logger from './logger';
import { ResourceBundle } from './schema';
import { buildMessageIndex } from './utils';
var debug = logger.extend('intlModule');
export var initialState = {
  locale: 'en-US',
  // This indicates the country detected for the incoming request, and is not
  // necessarily the user's actual country. If they're logged in, there will be
  // another place from which to read the correct country from their account.
  // Use this if you are interested in the geographic origin of the request
  // specifically.
  geoCountry: null,
  geoState: null,
  geoLocation: null,
  forceGeoCountry: null,
  forceGeoState: null,
  region: 'NA',
  resourceBundles: {},
  highlightBundles: false,
  ipAddress: ''
};
export function initIntl() {
  return function (dispatch, getState, context) {
    if (context.isServer && !context.hasPreloadedState) {
      var _context$req$context = context.req.context,
          locale = _context$req$context.locale,
          geoCountry = _context$req$context.geoCountry,
          geoState = _context$req$context.geoState,
          geoLocation = _context$req$context.geoLocation,
          ipAddress = _context$req$context.ipAddress;

      if (!locale) {
        throw new Error("The request does not have a locale. Have you installed the locale middleware? URL: ".concat(context.req.path));
      }

      dispatch(setLocale(locale));

      if (geoCountry) {
        dispatch(setGeoCountry({
          geoCountry: geoCountry
        }));
      }

      if (geoState) {
        dispatch(setGeoState({
          geoState: geoState
        }));
      }

      if (geoLocation) {
        dispatch(setGeoLocation(geoLocation));
      }

      if (ipAddress) {
        dispatch(setIpAddress(ipAddress));
      }

      var regions = config.get('public.intl.regions');
      var tld = getState().domain.tld;
      var region = regions[tld];

      if (region) {
        debug("Selected region: ".concat(tld, " -> ").concat(region));
        dispatch(setRegion(region));
      } // Support both camelcase and underscore param name.


      var highlightBundles = context.req.query.highlightBundles || context.req.query.highlight_bundles || '';

      if (/^(true|1)$/.test(highlightBundles)) {
        dispatch(setHighlightBundles(true));
      }
    }
  };
}
export var setLocale = createAction('intl/setLocale');
export var setGeoCountry = createAction('intl/setGeoCountry');
export var setGeoState = createAction('intl/setGeoState');
export var setRegion = createAction('intl/setRegion');
export var setGeoLocation = createAction('intl/setGeoLocation');
export var setHighlightBundles = createAction('intl/setHighlightBundles');
export var setIpAddress = createAction('intl/setIpAddress');
export var loadResourceBundlesRequest = createAction('intl/loadResourceBundlesRequest');
export var loadResourceBundlesSuccess = createAction('intl/loadResourceBundlesSuccess');
export var loadResourceBundlesFailure = createAction('intl/loadResourceBundlesFailure');
export function loadResourceBundles(groupCodes) {
  var meta = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (groupCodes && typeof groupCodes === 'string') {
    groupCodes = [groupCodes];
  } else if (!groupCodes || !groupCodes.length) {
    throw new Error("loadResourceBundles must be called with at least one group code");
  }

  var maxAge = config.get('public.intl.maxAge');
  return function (dispatch, getState) {
    var state = getState();
    var intl = state.intl;
    var locale = meta.locale || intl.locale;
    var localeBundles = intl.resourceBundles[locale];
    var bundlesToFetch = localeBundles ? [] : groupCodes;
    var bundlesFromCache = [];
    var now = getDateNowFunction(state)();

    if (localeBundles) {
      groupCodes.forEach(function (groupCode) {
        var bundle = localeBundles[groupCode];
        var shouldFetch = true;

        if (bundle) {
          var fetchedDate = parseDate(bundle.fetchedDate);
          var age = now - fetchedDate.getTime();

          if (age >= maxAge) {
            debug('Resource bundle “%s” is expired; refetching.', groupCode);
          } else {
            shouldFetch = false;
          }
        }

        if (shouldFetch) {
          bundlesToFetch.push(groupCode);
        } else {
          bundlesFromCache.push(groupCode);
        }
      });
    }

    if (bundlesToFetch.length) {
      return dispatch({
        bentoApi: {
          endpoint: 'resources',
          searchParams: {
            bundles: bundlesToFetch.join(' ')
          },
          headers: {
            'Accept-Language': locale
          },
          sessionRequired: false,
          schema: [ResourceBundle],
          actions: [loadResourceBundlesRequest, function (payload, meta) {
            return loadResourceBundlesSuccess(payload, _objectSpread(_objectSpread({}, meta), {}, {
              groupCodes: bundlesToFetch,
              locale: locale
            }));
          }, loadResourceBundlesFailure]
        }
      });
    }
  };
}
export var intlReducer = createReducer(initialState, (_createReducer = {}, _defineProperty(_createReducer, setLocale, function (state, action) {
  state.locale = action.payload;
}), _defineProperty(_createReducer, setGeoCountry, function (state, action) {
  var _action$payload = action.payload,
      geoCountry = _action$payload.geoCountry,
      forceGeoCountry = _action$payload.forceGeoCountry;

  if (typeof geoCountry !== 'undefined') {
    state.geoCountry = geoCountry;
  }

  if (typeof forceGeoCountry !== 'undefined') {
    state.forceGeoCountry = forceGeoCountry;
  }
}), _defineProperty(_createReducer, setGeoState, function (state, action) {
  var _action$payload2 = action.payload,
      geoState = _action$payload2.geoState,
      forceGeoState = _action$payload2.forceGeoState;

  if (typeof geoState !== 'undefined') {
    state.geoState = geoState;
  }

  if (typeof forceGeoState !== 'undefined') {
    state.forceGeoState = forceGeoState;
  }
}), _defineProperty(_createReducer, setRegion, function (state, action) {
  state.region = action.payload;
}), _defineProperty(_createReducer, setGeoLocation, function (state, action) {
  state.geoLocation = action.payload;
}), _defineProperty(_createReducer, setIpAddress, function (state, action) {
  state.ipAddress = action.payload;
}), _defineProperty(_createReducer, setHighlightBundles, function (state, action) {
  state.highlightBundles = action.payload;
}), _defineProperty(_createReducer, loadResourceBundlesSuccess, function (state, action) {
  var _action$meta = action.meta,
      fetchedDate = _action$meta.fetchedDate,
      groupCodes = _action$meta.groupCodes,
      locale = _action$meta.locale; // default all requested groupCodes to empty

  if (groupCodes && locale) {
    if (!state.resourceBundles[locale]) {
      state.resourceBundles[locale] = {};
    }

    groupCodes.forEach(function (groupCode) {
      var _buildMessageIndex = buildMessageIndex({}),
          keyIndex = _buildMessageIndex.keyIndex,
          values = _buildMessageIndex.values;

      state.resourceBundles[locale][groupCode] = {
        fetchedDate: fetchedDate,
        keyIndex: keyIndex,
        values: values
      };
    });
  }

  action.payload.forEach(function (bundle) {
    var locale = bundle.locale,
        groupCode = bundle.groupCode,
        resources = bundle.resources;

    if (!state.resourceBundles[locale]) {
      state.resourceBundles[locale] = {};
    }

    var _buildMessageIndex2 = buildMessageIndex(resources),
        keyIndex = _buildMessageIndex2.keyIndex,
        values = _buildMessageIndex2.values;

    state.resourceBundles[locale][groupCode] = {
      fetchedDate: fetchedDate,
      keyIndex: keyIndex,
      values: values
    };
  });
}), _createReducer));
/**
 * Get the `locale` prop or `intl.locale` from the store.
 */

export var getLocale = function getLocale(state, props) {
  return props && props.locale || state.intl.locale;
};
/**
 * Get the `region` prop or `intl.region` from the store.
 */

export var getRegion = function getRegion(state, props) {
  return props && props.region || state.intl.region;
};
export var getGeoCountry = function getGeoCountry(state) {
  return state.intl.forceGeoCountry || state.intl.geoCountry;
};
export var getGeoState = function getGeoState(state) {
  return state.intl.forceGeoState || state.intl.geoState;
};
export var getGeoLocation = function getGeoLocation(state) {
  return state.intl.geoLocation;
};
export var getIpAddress = function getIpAddress(state) {
  return state.intl.ipAddress;
};
/**
 * Get all loaded resource bundles from the store (including for other locales).
 */

export var getResourceBundles = function getResourceBundles(state) {
  return state.intl.resourceBundles;
};
/**
 * Get messages for the specified locale (from either prop or store) suitable
 * for being passed to `IntlProvider`.
 */

export var getMessages = createSelector([getLocale, getResourceBundles], function (locale, resourceBundles) {
  var messages = {};
  var localeBundles = resourceBundles[locale] || {};

  for (var groupCode in localeBundles) {
    var _localeBundles$groupC = localeBundles[groupCode],
        keyIndex = _localeBundles$groupC.keyIndex,
        values = _localeBundles$groupC.values;

    for (var key in keyIndex) {
      var index = keyIndex[key];
      var id = "".concat(groupCode, ".").concat(key);
      messages[id] = values[index];
    }
  }

  return messages;
});
export default {
  id: 'intl',
  reducerMap: {
    intl: intlReducer
  },
  initialActions: [initIntl()]
};